// POS entry — Point of Sales (cash) / Sales Entry (wholesale).
//
// Desktop parity: mirrors SalesWindow + PointOfSalesWindow + CheckoutWindow
// (appGUI). Item lookup uses the same Stock RPCs as the desktop grid
// (GetStockByReceiveID → GetStockPackingByBarCode → search), pricing follows
// StockTransactionRecord::ReassignPrice (promo/member/max-discount, floored at
// the configured reference cost), and saves go through SaveSales with a real
// POS session. Holds reserve stock (SuspendedSales / qohHoldOut) exactly like
// the desktop Alt+H / Suspend action.
//
// Recall/Edit: the parent hub passes `.recallRow` + `.recallToken` when the
// user hits Recall on an on-hold record, or Edit on a posted receipt/invoice.
//   - hold recall: desktop parity (SalesList::OnPrint) — the hold is deleted
//     as soon as it is loaded into this screen, so another desktop/PWA can no
//     longer recall the same hold. Should that delete fail, the id is kept and
//     the delete is retried when this cart is saved or re-held.
//   - posted edit (`.editMode`): the record is loaded with its id and the save
//     sends that id, so the store archives + replaces the same receipt/invoice
//     instead of creating a new one (desktop "Edit sales" parity).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  PosStockItem,
  PosPack,
  TradeServerKey,
  TradeSaleIn,
  TradeSalesRow,
} from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { printReceipt } from '../lib/receipt-print.js';
import { isRetailProfile } from '../lib/site-config.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

// ── helpers ───────────────────────────────────────────────────────────────

/** Retail-stall deployment — items carry one packing, so the pack chooser and
 *  the member/max-discount detail line are noise. */
const RETAIL = isRetailProfile();

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtMoney = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local-time yyyy-mm-dd for a timestamp (never the UTC-shifted date). */
function isoFromMs(ms: number): string {
  const d = new Date(ms);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Apply the store's rounding rules (desktop GetNearestFiveCent/TenCent). */
function applyRounding(total: number, sk: TradeServerKey, wholesale: boolean): number {
  const r = sk.rounding;
  const round5 = wholesale ? r.round5centsWholesales : r.round5cents;
  let rounded = total;
  if (round5) {
    rounded = Math.round(total / 0.05) * 0.05;
  } else if (r.sumToNearest > 0) {
    const step = r.sumToNearest / 100;
    rounded = r.truncateSum ? Math.floor(total / step) * step : Math.round(total / step) * step;
  }
  return round2(rounded - total);
}

interface PosLine {
  stockId: string;
  code: string;
  name: string;
  pack: string;
  packDesc: string;
  packSize: number;
  quantity: number;
  bonusQty: number;
  bonusPack: string;
  bonusPackDesc: string;
  price: number;
  listPrice: number;
  discPct: number;
  taxPct: number;
  tax: number;
  value: number;
  batchNo: string;
  expiryMs: number;
  // preserved when editing a posted sale so the store keeps the original
  // cost-of-sales and receive-batch attribution
  costValue?: number;
  receiveDetailId?: string;
  // pricing context (recompute on member/mode changes)
  sellingPrice: number;
  membershipPrice: number;
  maxDiscPct: number;
  promoDisc: number;
  costRefLoose: number;
  manualPrice: boolean;
}

interface KeptPosView {
  lines: PosLine[];
  memberId: string;
  memberName: string;
  memberCode: string;
  memberDiscount: number;
  memberTerm: number;
  memberCreditLimit: number;
  refNo: string;
  term: string;
  note: string;
  /** Channel carried from a recalled hold (empty = new cashier sale). */
  origin: string;
  discountAtTotalInput: string;
  transactionDate: string;
  // edit/recall context — must survive tab/outlet round-trips, otherwise
  // saving a restored edit would post a NEW sale, and a restored hold recall
  // would leave the old hold behind
  editingId: string;
  recallHoldId: string | null;
  loadedTxnMs: number;
}

interface PartyOption {
  id: string;
  description: string;
}

@customElement('pos-entry')
export class PosEntry extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .pos-wrap { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0.75rem 1rem 1rem; gap: 0.6rem; }

    .head-row { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: flex-end; flex-shrink: 0; }
    .head-row .fg { margin-bottom: 0; }
    .head-row .grow { flex: 1; min-width: 220px; }
    .chip-wrap { display: flex; align-items: center; gap: 0.5rem; min-height: 40px; }
    .party-chip { display: inline-flex; align-items: center; gap: 0.5rem; padding: 6px 12px; border-radius: 999px; border: 1px solid var(--color-border); background: var(--color-surface); font-size: 0.82rem; }
    .party-chip button { border: none; background: none; cursor: pointer; color: var(--color-text-secondary); font-size: 1rem; line-height: 1; }
    .credit-warn { font-size: 0.78rem; color: var(--color-error, #d93025); }

    .add-row { display: flex; gap: 0.5rem; flex-shrink: 0; position: relative; }
    .add-row input { flex: 1; }
    .search-pop {
      position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 60;
      background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px;
      max-height: 300px; overflow-y: auto; box-shadow: 0 16px 40px rgba(15,35,80,0.18);
    }
    .search-item { display: block; width: 100%; text-align: left; min-height: 44px; padding: 8px 12px; background: none; border: none; border-bottom: 1px solid var(--color-border); cursor: pointer; font-family: inherit; }
    .search-item:last-child { border-bottom: none; }
    .search-item:hover { background: #f4f7fb; }
    .si-name { display: block; font-size: 0.84rem; color: var(--color-text); }
    .si-sub { display: block; font-size: 0.7rem; color: var(--color-text-secondary); }

    .grid-wrap { flex: 1; min-height: 0; overflow: auto; border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); }
    .grid-wrap table { min-width: 860px; }
    .grid-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }
    td input { min-height: 32px; }
    .cell-num input { text-align: right; }
    .empty { padding: 2rem 1rem; text-align: center; color: var(--color-text-secondary); font-size: 0.85rem; }
    .rm { border: none; background: none; color: var(--color-error, #d93025); font-size: 1rem; cursor: pointer; }

    .footer-bar { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end; flex-shrink: 0; }
    .totals-box { display: flex; gap: 1.2rem; flex-wrap: wrap; align-items: flex-end; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 14px; padding: 0.6rem 1rem; }
    .totals-box .t { text-align: right; min-width: 92px; }
    .totals-box .t .lbl { display: block; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-secondary); font-weight: 700; }
    .totals-box .t .val { font-size: 0.95rem; font-weight: 600; }
    .totals-box .t.grand .val { font-size: 1.15rem; color: var(--color-primary); }
    .disc-input { max-width: 130px; }

    .notice { font-size: 0.8rem; color: var(--color-success, #188038); }
    .err { font-size: 0.8rem; color: var(--color-error, #d93025); }

    /* modals */
    .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; }
    .modal { background: var(--color-surface); border-radius: 16px; width: 100%; max-width: 560px; max-height: 88dvh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 24px 60px rgba(15,35,80,0.3); }
    .modal.wide { max-width: 720px; }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: none; border: none; border-radius: 10px; font-size: 1.2rem; cursor: pointer; color: var(--color-text-secondary); }
    .modal-close:hover { background: #f4f7fb; }
    .modal-body { padding: 1rem 1.1rem; overflow-y: auto; }
    .modal-footer { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .pick-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 0.6rem 0.9rem; }
    .pick-grid .fg { margin-bottom: 0; }
    .pay-line { display: flex; justify-content: space-between; align-items: center; padding: 0.45rem 0; border-bottom: 1px dashed var(--color-border); font-size: 0.9rem; }
    .pay-line.grand { border-bottom: none; font-weight: 700; font-size: 1.05rem; }
    .change { color: var(--color-success, #188038); }
    @media (max-width: 768px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh; }
      .modal-footer { padding-bottom: calc(0.9rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) mode: 'cash' | 'wholesale' = 'cash';
  @property({ type: Object }) recallRow: TradeSalesRow | null = null;
  @property({ type: Number }) recallToken = 0;
  /** True when recallRow is a POSTED record being edited (not a hold recall). */
  @property({ type: Boolean }) editMode = false;

  @state() private _sk: TradeServerKey | null = null;
  @state() private _skStore = '';
  @state() private _skFailed = '';
  @state() private _initError = '';
  @state() private _lines: PosLine[] = [];
  @state() private _error = '';
  @state() private _notice = '';
  @state() private _busy = false;

  // search + add
  @state() private _searchTerm = '';
  @state() private _searchResults: PosStockItem[] = [];
  @state() private _searchBusy = false;
  @state() private _searchOpen = false;

  // pick modal
  @state() private _pick: PosStockItem | null = null;
  @state() private _pickQty = '1';
  @state() private _pickPack = '';
  @state() private _pickPrice = '';
  @state() private _pickBonusQty = '';
  @state() private _pickBatch = '';
  @state() private _pickExpiry = '';

  // party (member / customer) picker
  @state() private _partyOpen = false;
  @state() private _partySearch = '';
  @state() private _partyResults: PartyOption[] = [];
  @state() private _partyBusy = false;
  @state() private _memberId = '';
  @state() private _memberName = '';
  @state() private _memberCode = '';
  @state() private _memberDiscount = 0;
  @state() private _memberTerm = 0;
  @state() private _memberCreditLimit = 0;

  // wholesale header
  @state() private _refNo = '';
  @state() private _term = '0';
  @state() private _note = '';
  @state() private _origin = '';
  @state() private _transactionDate = todayIso();

  @state() private _discInput = '';

  // payment modal
  @state() private _payOpen = false;
  @state() private _payById = '';
  @state() private _tendered = '';
  @state() private _reference = '';
  @state() private _payBusy = false;

  // receipt modal
  @state() private _savedSale: TradeSalesRow | null = null;
  @state() private _savedChange = 0;
  @state() private _savedUpdated = false;

  /** Posted sale being edited (sends its id on save); empty for a new sale. */
  @state() private _editingId = '';
  private _recallHoldId: string | null = null;
  private _lastRecallToken = 0;
  /** Original transaction timestamp loaded with a recall/edit (0 = none). */
  private _loadedTxnMs = 0;
  /** Payment type of the recalled/edited record (pre-selected on checkout). */
  private _preferredPayById = '';
  private _searchTimer: number | undefined;

  private _view = new OutletViewCache<KeptPosView>(this, 'trade-pos', {
    capture: () => ({
      lines: this._lines,
      memberId: this._memberId,
      memberName: this._memberName,
      memberCode: this._memberCode,
      memberDiscount: this._memberDiscount,
      memberTerm: this._memberTerm,
      memberCreditLimit: this._memberCreditLimit,
      refNo: this._refNo,
      term: this._term,
      note: this._note,
      origin: this._origin,
      discountAtTotalInput: this._discInput,
      transactionDate: this._transactionDate,
      editingId: this._editingId,
      recallHoldId: this._recallHoldId,
      loadedTxnMs: this._loadedTxnMs,
    }),
    restore: (v) => {
      this._lines = v.lines;
      this._memberId = v.memberId;
      this._memberName = v.memberName;
      this._memberCode = v.memberCode;
      this._memberDiscount = v.memberDiscount;
      this._memberTerm = v.memberTerm;
      this._memberCreditLimit = v.memberCreditLimit;
      this._refNo = v.refNo;
      this._term = v.term;
      this._note = v.note;
      this._origin = v.origin ?? '';
      this._discInput = v.discountAtTotalInput;
      this._transactionDate = v.transactionDate ?? todayIso();
      this._editingId = v.editingId ?? '';
      this._recallHoldId = v.recallHoldId ?? null;
      this._loadedTxnMs = v.loadedTxnMs ?? 0;
      this._busy = false;
      this._payOpen = false;
      this._pick = null;
      this._searchOpen = false;
      this._initError = '';
    },
    reset: () => {
      this._clearCart();
      this._busy = false;
      this._initError = '';
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode ? `${this.storeCode}|${this.mode}` : '');
    // Store settings are per outlet: fetch whenever the current outlet has
    // none yet — covers restored views and first attempts that were aborted.
    // A failed outlet is NOT auto-retried (avoids loops); the Retry button
    // clears _skFailed to allow another attempt.
    if (this.storeCode && this._skStore !== this.storeCode && this._skFailed !== this.storeCode) {
      void this._init();
    }
    // Recall hand-off from the on-hold list (same hub): recallToken changes
    // each time the user presses Recall, so re-recalls re-load too.
    if (this.recallRow && this.recallToken !== this._lastRecallToken) {
      this._lastRecallToken = this.recallToken;
      this._loadRecall(this.recallRow, this.editMode);
    }
  }

  private async _init() {
    if (!this.storeCode) return;
    const store = this.storeCode;
    const epoch = this._view.epoch;
    this._initError = '';
    try {
      const data = (await api.tradeServerKey(store)).data;
      if (!this._view.isCurrent(epoch) || this.storeCode !== store) return;
      this._sk = data;
      this._skStore = store;
      this._skFailed = '';
    } catch (e: any) {
      if (!this._view.isCurrent(epoch) || this.storeCode !== store) return;
      this._skFailed = store;
      this._initError = e?.message || 'Failed to load store settings';
    }
  }

  private _clearCart() {
    this._lines = [];
    this._memberId = '';
    this._memberName = '';
    this._memberCode = '';
    this._memberDiscount = 0;
    this._memberTerm = 0;
    this._memberCreditLimit = 0;
    this._refNo = '';
    this._term = '0';
    this._note = '';
    this._origin = '';
    this._discInput = '';
    this._transactionDate = todayIso();
    this._error = '';
    this._recallHoldId = null;
    this._editingId = '';
    this._loadedTxnMs = 0;
    this._preferredPayById = '';
  }

  private _storeName(): string {
    try {
      const raw = sessionStorage.getItem('ppos_session');
      if (raw) {
        const s = JSON.parse(raw);
        const db = s?.ownedDBs?.find((x: any) => x.code === this.storeCode);
        if (db?.name) return db.name;
      }
    } catch { /* ignore */ }
    return this.storeCode;
  }

  // ── pricing (StockTransactionRecord::ReassignPrice parity) ─────────────

  private _costRefLoose(item: PosStockItem): number {
    const ref = this._sk?.wholesale.referenceCost;
    if (ref === 'max') return item.maxCost ?? 0;
    if (ref === 'latest') return item.latestCost ?? 0;
    return item.weightedAvgCost ?? 0;
  }

  private _taxPct(item: PosStockItem): number {
    const gstType = this._sk?.gstTypeId;
    if (!gstType) return 0;
    const now = Date.now();
    const t = (item.taxAndCharges ?? []).find(
      (x) => x.typeId === gstType && (!x.fromDate || now >= x.fromDate) && (!x.toDate || now <= x.toDate));
    return t ? t.percentage / 100 : 0;
  }

  private _computePrice(item: PosStockItem, pack: PosPack): number {
    const sk = this._sk;
    const selling = pack.sellingPrice || 0;
    let price = selling;
    if ((item.promotionalDiscount ?? 0) > 0) price = Math.min(price, selling * (1 - (item.promotionalDiscount ?? 0) / 100));
    const isMember = !!this._memberId;
    if (isMember && pack.membershipPrice > 0 && pack.membershipPrice < selling)
      price = Math.min(price, pack.membershipPrice);
    const discApp = Math.min(
      Math.max(item.promotionalDiscount ?? 0, isMember ? this._memberDiscount : 0),
      pack.maxDiscountPercentage || 0,
    );
    if (discApp > 0) price = Math.min(price, selling * (1 - discApp / 100));
    const refCost = this._costRefLoose(item) * (pack.packSize || 1);
    if (this.mode === 'wholesale') {
      if (sk?.wholesale.useCost) price = refCost;
      if (price < refCost) price = refCost;
    } else if (price < refCost) {
      price = refCost;
    }
    return round2(price || 0);
  }

  private _priceLine(line: PosLine) {
    if (!line.manualPrice) {
      // recompute from stored context (promo/member/max-disc + cost floor)
      const selling = line.sellingPrice || 0;
      let price = selling;
      if (line.promoDisc > 0) price = Math.min(price, selling * (1 - line.promoDisc / 100));
      const isMember = !!this._memberId;
      if (isMember && line.membershipPrice > 0 && line.membershipPrice < selling)
        price = Math.min(price, line.membershipPrice);
      const discApp = Math.min(Math.max(line.promoDisc, isMember ? this._memberDiscount : 0), line.maxDiscPct || 0);
      if (discApp > 0) price = Math.min(price, selling * (1 - discApp / 100));
      const refCost = line.costRefLoose * (line.packSize || 1);
      if (this.mode === 'wholesale') {
        if (this._sk?.wholesale.useCost) price = refCost;
        if (price < refCost) price = refCost;
      } else if (price < refCost) {
        price = refCost;
      }
      line.price = round2(price || 0);
    }
    this._recalcLine(line);
  }

  private _recalcLine(line: PosLine) {
    const qty = Number(line.quantity) || 0;
    const net = qty * line.price;
    line.tax = round2(net * line.taxPct);
    line.value = round2(net + line.tax);
  }

  private _repriceAll() {
    this._lines = this._lines.map((l) => {
      const copy = { ...l };
      this._priceLine(copy);
      return copy;
    });
  }

  // ── item add ──────────────────────────────────────────────────────────

  private _onSearchInput(e: Event) {
    this._searchTerm = (e.target as HTMLInputElement).value;
    if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
    const term = this._searchTerm.trim();
    if (term.length < 2) {
      this._searchResults = [];
      this._searchOpen = false;
      return;
    }
    this._searchBusy = true;
    this._searchTimer = window.setTimeout(() => void this._runSearch(term), 300);
  }

  private async _runSearch(term: string) {
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockSearch(this.storeCode, term);
      if (!this._view.isCurrent(epoch)) return;
      this._searchResults = res.data ?? [];
      this._searchOpen = this._searchResults.length > 0;
    } catch {
      if (this._view.isCurrent(epoch)) this._searchResults = [];
    } finally {
      if (this._view.isCurrent(epoch)) this._searchBusy = false;
    }
  }

  private async _onSearchKey(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = this._searchTerm.trim();
    if (!term) return;
    // Typed code / barcode — desktop grid parity (GetStockByReceiveID etc.)
    // If the exact code does not resolve, fall back to a text search and let
    // the user pick from results instead of dead-ending on "not found".
    this._searchOpen = false;
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockByCode(this.storeCode, term);
      if (!this._view.isCurrent(epoch)) return;
      this._openPick(res.data, res.data.pack);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._searchBusy = true;
      await this._runSearch(term);
      if (!this._view.isCurrent(epoch)) return;
      if (this._searchOpen && this._searchResults.length > 0) {
        this._error = `No exact code match for '${term}' — pick an item from the results.`;
      } else {
        this._error = e?.message || `Item '${term}' not found`;
      }
    }
  }

  private async _pickSearchResult(item: PosStockItem) {
    this._searchOpen = false;
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockById(this.storeCode, item.id);
      if (!this._view.isCurrent(epoch)) return;
      this._openPick(res.data);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load item';
    }
  }

  private _openPick(item: PosStockItem, matchedPack?: string) {
    this._pick = item;
    this._pickQty = '1';
    this._pickBonusQty = '';
    this._pickBatch = item.batchNo || '';
    this._pickExpiry = item.expiryDate ? new Date(item.expiryDate).toISOString().slice(0, 7) : '';
    const packs = item.packRecords ?? [];
    const pack =
      packs.find((p) => matchedPack && p.packTypeCode === matchedPack) ??
      packs.find((p) => p.packTypeCode === item.pack) ??
      packs[0];
    this._pickPack = pack?.packTypeCode || '';
    this._pickPrice = pack ? String(this._computePrice(item, pack)) : '0';
    this._error = '';
  }

  private _pickPackRecord(): PosPack | undefined {
    const item = this._pick;
    if (!item) return undefined;
    return (item.packRecords ?? []).find((p) => p.packTypeCode === this._pickPack) ?? item.packRecords?.[0];
  }

  private _onPickPackChange(code: string) {
    this._pickPack = code;
    const item = this._pick;
    const pack = (item?.packRecords ?? []).find((p) => p.packTypeCode === code);
    if (item && pack) this._pickPrice = String(this._computePrice(item, pack));
  }

  private _addPicked() {
    const item = this._pick;
    const pack = this._pickPackRecord();
    if (!item || !pack) return;
    const qty = parseFloat(this._pickQty) || 0;
    if (qty <= 0) {
      this._error = 'Quantity must be greater than zero';
      return;
    }
    const price = parseFloat(this._pickPrice) || 0;
    const manual = Math.abs(price - this._computePrice(item, pack)) > 0.004;
    const line: PosLine = {
      stockId: item.id,
      code: item.code,
      name: item.name || item.displayName || '',
      pack: pack.packTypeCode,
      packDesc: pack.packDescription || pack.packTypeCode,
      packSize: pack.packSize || 1,
      quantity: qty,
      bonusQty: parseFloat(this._pickBonusQty) || 0,
      bonusPack: '',
      bonusPackDesc: '',
      price,
      listPrice: pack.sellingPrice || 0,
      discPct: 0,
      taxPct: this._taxPct(item),
      tax: 0,
      value: 0,
      batchNo: this._pickBatch,
      expiryMs: this._pickExpiry ? new Date(`${this._pickExpiry}-01T00:00:00`).getTime() : 0,
      sellingPrice: pack.sellingPrice || 0,
      membershipPrice: pack.membershipPrice || 0,
      maxDiscPct: pack.maxDiscountPercentage || 0,
      promoDisc: item.promotionalDiscount || 0,
      costRefLoose: this._costRefLoose(item),
      manualPrice: manual,
    };
    this._recalcLine(line);
    this._lines = [...this._lines, line];
    this._pick = null;
    this._searchTerm = '';
    this._searchResults = [];
    this._error = '';
  }

  private _removeLine(ix: number) {
    this._lines = this._lines.filter((_, i) => i !== ix);
  }

  private _updateLine(ix: number, patch: Partial<PosLine>) {
    this._lines = this._lines.map((l, i) => {
      if (i !== ix) return l;
      const next = { ...l, ...patch };
      if ('price' in patch) next.manualPrice = true;
      if ('discPct' in patch) {
        next.manualPrice = true;
        next.price = round2(next.sellingPrice * (1 - (Number(patch.discPct) || 0) / 100));
      }
      this._recalcLine(next);
      return next;
    });
  }

  // ── party picker (member for cash, customer for wholesale) ────────────

  private async _openParty() {
    this._partyOpen = true;
    this._partySearch = '';
    this._partyResults = [];
    await this._searchParties('');
  }

  private async _searchParties(q: string) {
    const epoch = this._view.epoch;
    this._partyBusy = true;
    try {
      if (this.mode === 'wholesale') {
        const res = await api.staffCompanies(this.storeCode, 'opscustomer', q);
        if (!this._view.isCurrent(epoch)) return;
        this._partyResults = (res.data ?? []).map((r) => ({ id: r.id, description: r.description }));
      } else {
        const res = await api.staffMembers(this.storeCode, q);
        if (!this._view.isCurrent(epoch)) return;
        this._partyResults = (res.data ?? []).map((r: any) => ({
          id: r.id,
          description: [r.name, r.code].filter(Boolean).join(' · '),
        }));
      }
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Search failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._partyBusy = false;
    }
  }

  private async _chooseParty(opt: PartyOption) {
    const epoch = this._view.epoch;
    this._partyBusy = true;
    try {
      if (this.mode === 'wholesale') {
        const res = await api.staffCompanyDetail(this.storeCode, 'opscustomer', opt.id);
        if (!this._view.isCurrent(epoch)) return;
        const c = res.data;
        this._memberId = c.id;
        this._memberName = c.name;
        this._memberCode = c.code;
        this._memberDiscount = Number(c.discount) || 0;
        this._memberTerm = Number(c.debitTerm) || 0;
        this._memberCreditLimit = Number(c.creditLimit) || 0;
        if (this._memberTerm) this._term = String(this._memberTerm);
      } else {
        const res = await api.staffMemberDetail(this.storeCode, opt.id);
        if (!this._view.isCurrent(epoch)) return;
        const m = res.data;
        this._memberId = m.id;
        this._memberName = m.name;
        this._memberCode = m.code;
        this._memberDiscount = Number(m.discount) || 0;
        this._memberTerm = Number(m.debitTerm) || 0;
        this._memberCreditLimit = Number(m.creditLimit) || 0;
      }
      this._partyOpen = false;
      this._repriceAll();
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load record';
    } finally {
      if (this._view.isCurrent(epoch)) this._partyBusy = false;
    }
  }

  private _clearParty() {
    this._memberId = '';
    this._memberName = '';
    this._memberCode = '';
    this._memberDiscount = 0;
    this._memberTerm = 0;
    this._memberCreditLimit = 0;
    this._repriceAll();
  }

  // ── totals ────────────────────────────────────────────────────────────

  private get _net(): number {
    return round2(this._lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.price) || 0), 0));
  }
  private get _tax(): number {
    return round2(this._lines.reduce((s, l) => s + (Number(l.tax) || 0), 0));
  }
  private get _rounding(): number {
    if (!this._sk) return 0;
    return applyRounding(this._net + this._tax, this._sk, this.mode === 'wholesale');
  }
  private get _discAtTotal(): number {
    const v = parseFloat(this._discInput) || 0;
    return v > 0 ? -round2(v) : 0;
  }
  private get _due(): number {
    return round2(this._net + this._tax + this._rounding + this._discAtTotal);
  }
  private get _creditWarn(): boolean {
    return this.mode === 'wholesale'
      && !!this._sk?.wholesale.enableCreditLimitCheck
      && this._memberCreditLimit > 0
      && this._net + this._tax > this._memberCreditLimit;
  }

  // ── save / hold ───────────────────────────────────────────────────────

  private _buildPayload(): TradeSaleIn {
    const items = this._lines.map((l) => ({
      stockId: l.stockId,
      pack: l.pack,
      quantity: Number(l.quantity) || 0,
      bonusQty: l.bonusQty || 0,
      bonusPack: l.bonusPack || '',
      price: l.price,
      listPrice: l.listPrice,
      discount: 0,
      tax: l.tax,
      value: l.value,
      ...(l.costValue !== undefined ? { costValue: l.costValue } : {}),
      batchNo: l.batchNo || '',
      expiryDate: l.expiryMs || 0,
      ...(l.receiveDetailId ? { receiveDetailId: l.receiveDetailId } : {}),
    }));
    const payload: TradeSaleIn = {
      ...(this._editingId ? { id: this._editingId } : {}),
      type: this.mode === 'wholesale' ? 'credit' : 'cash',
      items,
      customerId: this._memberId || undefined,
      refNo: this._refNo || undefined,
      note: this._note || undefined,
      origin: this._origin || undefined,
      term: parseFloat(this._term) || 0,
      rounding: this._rounding || undefined,
      discountAtTotal: this._discAtTotal || undefined,
    };
    // Keep the original timestamp when the date field was not touched; a new
    // date uses the current time of day (same as a fresh sale).
    const unchanged = this._loadedTxnMs > 0 && this._transactionDate === isoFromMs(this._loadedTxnMs);
    const when = unchanged
      ? this._loadedTxnMs
      : this._transactionDate
        ? new Date(`${this._transactionDate}T${new Date().toTimeString().slice(0, 8)}`).getTime()
        : 0;
    if (when) payload.transactionDate = when;
    return payload;
  }

  private _validate(): string {
    if (!this._sk) return 'Store settings not loaded';
    if (this._lines.length === 0) return 'Add at least one item';
    if (this.mode === 'wholesale' && !this._memberId) return 'Select a customer for the wholesale sale';
    return '';
  }

  private async _hold() {
    if (this._editingId) {
      this._error = 'Holding is not available while editing a posted record — save it to update.';
      return;
    }
    const invalid = this._validate();
    if (invalid) {
      this._error = invalid;
      return;
    }
    this._busy = true;
    this._error = '';
    this._notice = '';
    const epoch = this._view.epoch;
    try {
      const res = await api.tradeHoldSale(this.storeCode, this._buildPayload());
      if (!this._view.isCurrent(epoch)) return;
      await this._releaseRecalledHold();
      this._notice = `${this.mode === 'wholesale' ? 'Invoice' : 'Receipt'} on hold — #${res.data.seqNo || res.data.id}`;
      this._clearCart();
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Hold failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._busy = false;
    }
  }

  /** Safety net: delete a recalled hold whose immediate release did not land. */
  private async _releaseRecalledHold() {
    const holdId = this._recallHoldId;
    this._recallHoldId = null;
    if (!holdId) return;
    try {
      await api.tradeDeleteSale(this.storeCode, holdId, 'suspend');
    } catch {
      // The new record is saved; the old hold may linger — the list shows it
      // and it can be deleted manually. Never fail the save because of this.
    }
  }

  /**
   * Desktop parity (SalesList::OnPrint → DeleteSales right after Reload*):
   * delete the hold as soon as it is loaded into this screen, so another
   * desktop/PWA cannot recall the same hold. On failure the id is kept so
   * `_releaseRecalledHold` retries when this cart is saved or re-held.
   */
  private async _releaseRecalledHoldNow(storeCode: string, holdId: string) {
    try {
      await api.tradeDeleteSale(storeCode, holdId, 'suspend');
      if (this._recallHoldId === holdId) this._recallHoldId = null;
    } catch {
      if (this._recallHoldId === holdId && this.storeCode === storeCode) {
        this._notice = 'The recalled hold could not be removed — it will be released when you save or re-hold';
      }
    }
  }

  private _openPay() {
    const invalid = this._validate();
    if (invalid) {
      this._error = invalid;
      return;
    }
    const payTypes = this._sk?.payTypes ?? [];
    if (!payTypes.length) {
      this._error = 'No payment types configured for this outlet';
      return;
    }
    const preferred = this._preferredPayById
      ? payTypes.find((p) => p.id === this._preferredPayById)
      : undefined;
    const cash = preferred ?? payTypes.find((p) => /cash/i.test(p.name)) ?? payTypes[0];
    this._payById = cash.id;
    this._tendered = this._due > 0 ? String(this._due) : '';
    this._reference = '';
    this._error = '';
    this._payOpen = true;
  }

  private get _change(): number {
    const t = parseFloat(this._tendered) || 0;
    return round2(Math.max(0, t - this._due));
  }

  private _payValid(): boolean {
    const t = parseFloat(this._tendered) || 0;
    return t >= this._due - 0.004 && this._due >= -0.004;
  }

  private async _confirmPay() {
    if (!this._payValid()) {
      this._error = 'Tendered amount is less than the amount due';
      return;
    }
    this._payBusy = true;
    this._error = '';
    const epoch = this._view.epoch;
    const editing = !!this._editingId;
    this._savedUpdated = false;
    const payload = this._buildPayload();
    payload.payments = [{
      payById: this._payById,
      amount: this._due,
      tendered: parseFloat(this._tendered) || this._due,
      reference: this._reference || undefined,
    }];
    try {
      const res = await api.tradeCompleteSale(this.storeCode, payload);
      if (!this._view.isCurrent(epoch)) return;
      await this._releaseRecalledHold();
      // Load the saved record for the receipt (lines + seq come from the store).
      let saved: TradeSalesRow | null = null;
      try {
        saved = (await api.tradeSalesDetail(this.storeCode, res.data.id, 'sales')).data;
      } catch { /* fall back to a minimal receipt below */ }
      if (!this._view.isCurrent(epoch)) return;
      const change = this._change;
      this._savedChange = change;
      this._savedUpdated = editing;
      this._savedSale = saved ?? ({
        id: res.data.id,
        seqNo: res.data.seqNo,
        date: Date.now(),
        total: this._net,
        tax: this._tax,
        rounding: this._rounding,
        discountAtTotal: this._discAtTotal,
        term: parseFloat(this._term) || 0,
        refNo: this._refNo,
        note: this._note,
        salesTypeId: '',
        customerId: this._memberId,
        customerName: this._memberName,
        userName: '',
        payments: [{ paidById: this._payById, paidByCode: '', paidByName: '', amount: this._due, tendered: parseFloat(this._tendered) || this._due, reference: this._reference, points: 0, date: Date.now() }],
        lines: this._lines.map((l) => ({
          stockId: l.stockId, code: l.code, name: l.name, pack: l.pack, quantity: l.quantity,
          bonusQty: l.bonusQty, price: l.price, value: l.value, tax: l.tax,
          packDesc: l.packDesc, packSize: l.packSize,
        })),
      } as TradeSalesRow);
      this._payOpen = false;
      this._clearCart();
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Sale failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._payBusy = false;
    }
  }

  private async _downloadReceiptPdf() {
    const sale = this._savedSale;
    if (!sale) return;
    try {
      const blob = await api.tradeSalesInvoicePdf(this.storeCode, sale.id, {});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${sale.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e: any) {
      this._error = e?.message || 'Invoice failed';
    }
  }

  // ── recall ────────────────────────────────────────────────────────────

  private _loadRecall(row: TradeSalesRow, edit: boolean) {
    const lines: PosLine[] = (row.lines ?? []).map((l) => {
      const line: PosLine = {
        stockId: l.stockId,
        code: l.code,
        name: l.name,
        pack: l.pack,
        packDesc: l.packDesc || l.pack,
        packSize: l.packSize ?? 1,
        quantity: l.quantity,
        bonusQty: l.bonusQty ?? 0,
        bonusPack: l.bonusPack || '',
        bonusPackDesc: '',
        price: l.price ?? 0,
        // Fall back to the paid price when an old row stored 0 in priceB4disc —
        // sending 0 would let the store treat the line as "unpriced".
        listPrice: l.listPrice || l.price || 0,
        discPct: 0,
        taxPct: l.quantity ? (Number(l.tax) || 0) / (Math.max(l.quantity * (l.price ?? 0), 0.0001)) : 0,
        tax: l.tax ?? 0,
        value: l.value ?? 0,
        batchNo: l.batchNo || '',
        expiryMs: l.expiryDate || 0,
        costValue: l.costValue,
        receiveDetailId: l.receiveDetailId,
        sellingPrice: l.listPrice || l.price || 0,
        membershipPrice: 0,
        maxDiscPct: 0,
        promoDisc: 0,
        costRefLoose: 0,
        manualPrice: true, // keep the saved prices as-is
      };
      return line;
    });
    this._lines = lines;
    this._memberId = row.customerId || '';
    this._memberName = row.customerName || '';
    this._memberCode = row.customerCode || '';
    this._memberDiscount = 0;
    this._memberTerm = 0;
    this._memberCreditLimit = 0;
    this._refNo = row.refNo || '';
    this._note = row.note || '';
    this._origin = row.origin || '';
    this._term = String(row.term || 0);
    this._discInput = row.discountAtTotal ? String(-row.discountAtTotal) : '';
    // Keep the original transaction date on both recall and edit.
    this._loadedTxnMs = row.date || 0;
    this._transactionDate = row.date ? isoFromMs(row.date) : todayIso();
    this._recallHoldId = edit ? null : row.id;
    this._editingId = edit ? row.id : '';
    this._preferredPayById = row.payments?.[0]?.paidById || '';
    this._error = '';
    this._notice = edit
      ? `Editing ${this.mode === 'wholesale' ? 'invoice' : 'receipt'} #${row.seqNo || row.id} — save to update this record`
      : `Loaded ${this.mode === 'wholesale' ? 'on-hold invoice' : 'on-hold receipt'} #${row.seqNo || row.id} — hold released`;
    void this._hydrateRecallParty();
    // Desktop parity: release the hold now that this screen owns the lines, so
    // no other desktop/PWA can recall it again. The store is captured here —
    // the panel is reused when the user switches outlet.
    if (!edit) void this._releaseRecalledHoldNow(this.storeCode, row.id);
  }

  /** Best-effort: fetch the member/customer discount context for re-priced lines. */
  private async _hydrateRecallParty() {
    if (!this._memberId) return;
    try {
      if (this.mode === 'wholesale') {
        const c = (await api.staffCompanyDetail(this.storeCode, 'opscustomer', this._memberId)).data;
        this._memberDiscount = Number(c.discount) || 0;
        this._memberTerm = Number(c.debitTerm) || 0;
        this._memberCreditLimit = Number(c.creditLimit) || 0;
      } else {
        const m = (await api.staffMemberDetail(this.storeCode, this._memberId)).data;
        this._memberDiscount = Number(m.discount) || 0;
      }
    } catch { /* keep zero context */ }
  }

  // ── render ────────────────────────────────────────────────────────────

  render() {
    if (!this.storeCode) return html`<div class="empty">No outlet selected.</div>`;
    const ready = !!this._sk && this._skStore === this.storeCode;
    if (!ready && this._skFailed === this.storeCode) {
      return html`
        <div class="empty">
          ⚠ ${this._initError || 'Failed to load store settings'}
          <div style="margin-top:0.75rem">
            <button class="btn btn-sm" @click=${() => { this._skFailed = ''; void this._init(); }}>Retry</button>
          </div>
        </div>`;
    }
    if (!ready) {
      return html`<div class="empty">Loading store settings…</div>`;
    }
    return html`
      <div class="pos-wrap">
        <div class="head-row">
          <div class="chip-wrap">
            <span class="party-chip">
              ${this.mode === 'wholesale' ? '🧑 Customer:' : '👤 Member:'}
              <strong>${this._memberName || '—'}</strong>
              ${this._memberId ? html`<button title="Clear" @click=${() => this._clearParty()}>&times;</button>` : ''}
            </span>
            <button class="btn btn-sm" @click=${() => void this._openParty()}>
              ${this.mode === 'wholesale' ? 'Select Customer' : 'Attach Member'}
            </button>
            ${this._memberId && this._memberDiscount ? html`<span style="font-size:0.75rem;color:var(--color-text-secondary)">disc ${this._memberDiscount}%</span>` : ''}
          </div>
          ${this.mode === 'wholesale' ? html`
            <div class="fg"><label>Invoice No</label>
              <input .value=${this._refNo} @input=${(e: Event) => { this._refNo = (e.target as HTMLInputElement).value; }}></div>
            <div class="fg" style="max-width:110px"><label>Term</label>
              <input type="number" .value=${this._term} @change=${(e: Event) => { this._term = (e.target as HTMLInputElement).value; }}></div>
          ` : ''}
          <div class="fg"><label>Date</label>
            <input type="date" .value=${this._transactionDate} @change=${(e: Event) => { this._transactionDate = (e.target as HTMLInputElement).value; }}></div>
          <div class="fg grow"><label>Note</label>
            <input .value=${this._note} @input=${(e: Event) => { this._note = (e.target as HTMLInputElement).value; }}></div>
          ${this._creditWarn ? html`<div class="credit-warn">⚠ Exceeds customer credit limit (${fmtMoney(this._memberCreditLimit)})</div>` : ''}
        </div>

        <div class="add-row">
          <input placeholder="Scan / type item code (Enter to add) or search…"
            data-testid="pos-search"
            .value=${this._searchTerm}
            @input=${this._onSearchInput}
            @keydown=${this._onSearchKey} />
          ${this._searchBusy ? html`<span style="align-self:center;font-size:0.75rem;color:var(--color-text-secondary)">…</span>` : ''}
          ${this._searchOpen ? html`
            <div class="search-pop">
              ${this._searchResults.map((r) => html`
                <button class="search-item" data-testid="pos-search-result" @click=${() => void this._pickSearchResult(r)}>
                  <span class="si-name">${r.name}${r.displayName && r.displayName !== r.name ? ` · ${r.displayName}` : ''}</span>
                  <span class="si-sub">${r.code}${r.typeName ? ` · ${r.typeName}` : ''} · QOH ${r.qohString ?? r.qoh ?? '—'}${r.packRecords?.length ? ` · ${fmtMoney(r.packRecords[0].sellingPrice)}` : ''}</span>
                </button>
              `)}
            </div>
          ` : ''}
        </div>

        ${this._error ? html`<div class="err">⚠ ${this._error}</div>` : ''}
        ${this._notice ? html`<div class="notice">✓ ${this._notice}</div>` : ''}

        <div class="grid-wrap">
          <table>
            <thead>
              <tr>
                <th style="width:90px">Code</th>
                <th>Item</th>
                <th class="num" style="width:80px">Qty</th>
                <th style="width:80px">Pack</th>
                ${this.mode === 'wholesale' ? html`
                  <th class="num" style="width:70px">Bonus</th>
                  <th style="width:110px">Batch</th>
                  <th style="width:120px">Expiry</th>
                ` : ''}
                <th class="num" style="width:90px">Price</th>
                <th class="num" style="width:70px">Disc%</th>
                <th class="num" style="width:90px">Total</th>
                <th style="width:36px"></th>
              </tr>
            </thead>
            <tbody>
              ${this._lines.length === 0 ? html`
                <tr><td colspan=${this.mode === 'wholesale' ? 11 : 8}><div class="empty">Scan or search an item to start.</div></td></tr>
              ` : this._lines.map((l, ix) => html`
                <tr>
                  <td>${l.code}</td>
                  <td>${l.name}</td>
                  <td class="cell-num"><input type="number" step="0.001" min="0" data-testid="pos-qty" .value=${String(l.quantity)}
                    @change=${(e: Event) => this._updateLine(ix, { quantity: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td>${l.packDesc}</td>
                  ${this.mode === 'wholesale' ? html`
                    <td class="cell-num"><input type="number" step="0.001" min="0" .value=${String(l.bonusQty || '')}
                      @change=${(e: Event) => this._updateLine(ix, { bonusQty: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                    <td><input .value=${l.batchNo}
                      @change=${(e: Event) => this._updateLine(ix, { batchNo: (e.target as HTMLInputElement).value })}></td>
                    <td><input type="month" .value=${l.expiryMs ? new Date(l.expiryMs).toISOString().slice(0, 7) : ''}
                      @change=${(e: Event) => {
                        const v = (e.target as HTMLInputElement).value;
                        this._updateLine(ix, { expiryMs: v ? new Date(`${v}-01T00:00:00`).getTime() : 0 });
                      }}></td>
                  ` : ''}
                  <td class="cell-num"><input type="number" step="0.01" min="0" .value=${String(l.price)}
                    @change=${(e: Event) => this._updateLine(ix, { price: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td class="cell-num"><input type="number" step="1" min="0" .value=${String(l.discPct || '')}
                    @change=${(e: Event) => this._updateLine(ix, { discPct: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td class="num">${fmtMoney(l.value)}</td>
                  <td><button class="rm" title="Remove" @click=${() => this._removeLine(ix)}>✕</button></td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>

        <div class="footer-bar">
          <div class="totals-box">
            <div class="t"><span class="lbl">Subtotal</span><span class="val">${fmtMoney(this._net)}</span></div>
            <div class="t"><span class="lbl">Tax</span><span class="val">${fmtMoney(this._tax)}</span></div>
            <div class="t"><span class="lbl">Rounding</span><span class="val">${fmtMoney(this._rounding)}</span></div>
            <div class="t disc-input"><span class="lbl">Disc At Total</span>
              <input type="number" step="0.01" min="0" .value=${this._discInput}
                @input=${(e: Event) => { this._discInput = (e.target as HTMLInputElement).value; }}></div>
            <div class="t grand"><span class="lbl">Amount Due</span><span class="val">${fmtMoney(this._due)}</span></div>
          </div>
          <button class="btn btn-sec" ?disabled=${this._busy || this._lines.length === 0 || !!this._editingId}
            data-testid="pos-hold"
            title=${this._editingId ? 'Holding is not available while editing a posted record' : ''}
            @click=${() => void this._hold()}>
            ${this._busy ? '…' : '⏸ Hold'}
          </button>
          <button class="btn" style="min-height:48px;font-size:0.95rem" data-testid="pos-pay"
            ?disabled=${this._busy || this._lines.length === 0} @click=${() => this._openPay()}>
            ${this._editingId ? '💾 Update' : '💰 Pay'} ${fmtMoney(this._due)}
          </button>
        </div>
      </div>

      ${this._pick ? this._renderPick() : ''}
      ${this._partyOpen ? this._renderParty() : ''}
      ${this._payOpen ? this._renderPay() : ''}
      ${this._savedSale ? this._renderReceipt() : ''}
    `;
  }

  private _renderPick() {
    const item = this._pick!;
    const packs = item.packRecords ?? [];
    const pack = this._pickPackRecord();
    // Retail stalls sell a single packing — the chooser adds nothing then.
    const singlePack = RETAIL && packs.length === 1;
    return html`
      <div class="modal-overlay" @click=${() => { this._pick = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${item.name}</h3>
            <button class="modal-close" @click=${() => { this._pick = null; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div style="font-size:0.78rem;color:var(--color-text-secondary);margin-bottom:0.75rem">
              ${item.code}${item.typeName ? ` · ${item.typeName}` : ''} · QOH ${item.qohString ?? item.qoh ?? '—'}
              ${item.qohHold ? ` · Held ${item.qohHold}` : ''}
            </div>
            <div class="pick-grid">
              ${singlePack
                ? ''
                : html`
                    <div class="fg"><label>Pack</label>
                      <select .value=${this._pickPack} @change=${(e: Event) => this._onPickPackChange((e.target as HTMLSelectElement).value)}>
                        ${packs.map((p) => html`
                          <option value=${p.packTypeCode} ?selected=${p.packTypeCode === this._pickPack}>
                            ${p.packDescription || p.packTypeCode}${p.packSize ? ` (x${p.packSize})` : ''}
                          </option>`)}
                      </select></div>`}
              <div class="fg"><label>Quantity</label>
                <input type="number" step="0.001" min="0" .value=${this._pickQty}
                  @input=${(e: Event) => { this._pickQty = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Price</label>
                <input type="number" step="0.01" min="0" .value=${this._pickPrice}
                  @input=${(e: Event) => { this._pickPrice = (e.target as HTMLInputElement).value; }}></div>
              ${this.mode === 'wholesale' ? html`
                <div class="fg"><label>Bonus Qty</label>
                  <input type="number" step="0.001" min="0" .value=${this._pickBonusQty}
                    @input=${(e: Event) => { this._pickBonusQty = (e.target as HTMLInputElement).value; }}></div>
                <div class="fg"><label>Batch No</label>
                  <input .value=${this._pickBatch}
                    @input=${(e: Event) => { this._pickBatch = (e.target as HTMLInputElement).value; }}></div>
                <div class="fg"><label>Expiry</label>
                  <input type="month" .value=${this._pickExpiry}
                    @input=${(e: Event) => { this._pickExpiry = (e.target as HTMLInputElement).value; }}></div>
              ` : ''}
            </div>
            ${pack ? html`
              <div style="margin-top:0.6rem;font-size:0.75rem;color:var(--color-text-secondary)">
                ${singlePack
                  ? html`Selling ${fmtMoney(pack.sellingPrice)}
                      ${item.promotionalDiscount ? ` · Promo ${item.promotionalDiscount}%` : ''}`
                  : html`Selling ${fmtMoney(pack.sellingPrice)} · Member ${fmtMoney(pack.membershipPrice)} · Max disc ${pack.maxDiscountPercentage}%
                      ${item.promotionalDiscount ? ` · Promo ${item.promotionalDiscount}%` : ''}
                      · Cost ref ${fmtMoney(this._costRefLoose(item))}`}
              </div>` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => { this._pick = null; }}>Cancel</button>
            <button class="btn-submit" data-testid="pos-add-item" @click=${() => this._addPicked()}>Add Item</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderParty() {
    return html`
      <div class="modal-overlay" @click=${() => { this._partyOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${this.mode === 'wholesale' ? 'Select Customer' : 'Attach Member'}</h3>
            <button class="modal-close" @click=${() => { this._partyOpen = false; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="fg">
              <input placeholder="Search name / code / phone…" .value=${this._partySearch}
                @input=${(e: Event) => {
                  this._partySearch = (e.target as HTMLInputElement).value;
                  if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
                  this._searchTimer = window.setTimeout(() => void this._searchParties(this._partySearch.trim()), 300);
                }} />
            </div>
            <div>
              ${this._partyBusy ? html`<div style="padding:0.6rem;font-size:0.8rem;color:var(--color-text-secondary)">Searching…</div>` : ''}
              ${!this._partyBusy && this._partyResults.length === 0 ? html`
                <div style="padding:0.6rem;font-size:0.8rem;color:var(--color-text-secondary)">No matches.</div>` : ''}
              ${this._partyResults.map((r) => html`
                <button class="search-item" @click=${() => void this._chooseParty(r)}>
                  <span class="si-name">${r.description}</span>
                </button>
              `)}
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => { this._partyOpen = false; }}>Close</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderPay() {
    const payTypes = this._sk?.payTypes ?? [];
    return html`
      <div class="modal-overlay" @click=${() => { if (!this._payBusy) this._payOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${this.mode === 'wholesale' ? 'Payment' : 'Payment'}</h3>
            <button class="modal-close" @click=${() => { if (!this._payBusy) this._payOpen = false; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="pay-line"><span>Subtotal</span><span>${fmtMoney(this._net)}</span></div>
            ${this._tax ? html`<div class="pay-line"><span>Tax</span><span>${fmtMoney(this._tax)}</span></div>` : ''}
            ${this._rounding ? html`<div class="pay-line"><span>Rounding</span><span>${fmtMoney(this._rounding)}</span></div>` : ''}
            ${this._discAtTotal ? html`<div class="pay-line"><span>Discount</span><span>${fmtMoney(this._discAtTotal)}</span></div>` : ''}
            <div class="pay-line grand"><span>Amount Due</span><span>${fmtMoney(this._due)}</span></div>
            <div class="pick-grid" style="margin-top:0.75rem">
              <div class="fg"><label>Pay By</label>
                <select data-testid="pos-pay-by" .value=${this._payById} @change=${(e: Event) => { this._payById = (e.target as HTMLSelectElement).value; }}>
                  ${payTypes.map((p) => html`<option value=${p.id} ?selected=${p.id === this._payById}>${p.name}</option>`)}
                </select></div>
              <div class="fg"><label>Amount Tendered</label>
                <input type="number" step="0.01" min="0" .value=${this._tendered}
                  data-testid="pos-tendered"
                  @input=${(e: Event) => { this._tendered = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Reference</label>
                <input .value=${this._reference}
                  @input=${(e: Event) => { this._reference = (e.target as HTMLInputElement).value; }}></div>
            </div>
            <div class="pay-line" style="margin-top:0.6rem"><span>Change</span><span class="change">${fmtMoney(this._change)}</span></div>
            ${this._error ? html`<div class="err" style="margin-top:0.5rem">⚠ ${this._error}</div>` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" ?disabled=${this._payBusy} @click=${() => { this._payOpen = false; }}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._payBusy || !this._payValid()} data-testid="pos-confirm-pay"
              @click=${() => void this._confirmPay()}>
              ${this._payBusy ? 'Saving…' : `Confirm ${fmtMoney(this._due)}`}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderReceipt() {
    const sale = this._savedSale!;
    return html`
      <div class="modal-overlay" data-testid="pos-receipt" @click=${() => { this._savedSale = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${this.mode === 'wholesale' ? 'Invoice' : 'Receipt'} #${sale.seqNo || sale.id} ${this._savedUpdated ? 'updated' : 'saved'}</h3>
            <button class="modal-close" @click=${() => { this._savedSale = null; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="pay-line"><span>Total</span><span>${fmtMoney(sale.total + (sale.tax || 0) + (sale.rounding || 0) + (sale.discountAtTotal || 0))}</span></div>
            ${this._savedChange > 0 ? html`<div class="pay-line"><span>Change</span><span class="change">${fmtMoney(this._savedChange)}</span></div>` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn btn-sec" @click=${() => printReceipt(sale, this._storeName(), { wholesale: this.mode === 'wholesale', change: this._savedChange })}>🖨 Print</button>
            <button class="btn btn-sec" data-testid="pos-receipt-pdf" @click=${() => void this._downloadReceiptPdf()}>⬇ PDF</button>
            <button class="btn-submit" data-testid="pos-new-sale" @click=${() => { this._savedSale = null; }}>New Sale</button>
          </div>
        </div>
      </div>
    `;
  }
}
